import * as R from 'ramda';

import { CreatedElement, DraftAsset, DraftElement } from '@atom/types/ai';
import { AssetCreateAttributesInput } from '@atom/types/inventory';
import { Schema, SchemaTree } from '@atom/types/schema';
import { getAttributeErrors } from '@atom/utilities/assetUtilities';
import { getSchemaAttributesMap } from '@atom/utilities/schemaUtilities';
import { combineSets } from '@atom/utilities/setUtilities';

export type ValidationResult = {
  errorsByPath: Map<string, Set<string>>;
  ancestorPathsToExpand: Set<string>;
};

// strips draft-specific properties off attributes
export const mapWorkflowAttributesToAttributes = (
  attributes: DraftElement['attributes'],
): AssetCreateAttributesInput =>
  Object.entries(attributes).reduce(
    (acc, [id, attr]) => ({ ...acc, [id]: { value: attr.value } }),
    {} as AssetCreateAttributesInput,
  );

// gets all ancestor paths from a given path
const getAncestorPaths = (path: (string | number)[]): Set<string> =>
  new Set(R.range(1, path.length).map(idx => path.slice(0, idx).join('.')));

// checks if ancestor is in given set
export const isAncestorInSet = (pathKey: string, set: Set<string>): boolean => {
  if (!pathKey || !set.size) {
    return false;
  }

  const parts = pathKey.split('.');

  return R.range(1, parts.length).some(idx =>
    set.has(parts.slice(0, idx).join('.')),
  );
};

// walks the draft asset and all elements recursively and validates attributes
// returns all paths where errors occurred and a set of ancestor paths to expand
// so that users can see where errors are
export const validateDraftAssetTree = (
  draftAsset: DraftAsset,
  schemaIdToSchema: Map<string, SchemaTree>,
  excludedPaths: Set<string>,
): ValidationResult => {
  const errorsByPath = new Map<string, Set<string>>();
  let ancestorPathsToExpand = new Set<string>();

  const walk = (
    node: DraftAsset | DraftElement,
    path: (string | number)[],
  ): void => {
    const pathKey = path.join('.');
    if (excludedPaths.has(pathKey) || isAncestorInSet(pathKey, excludedPaths)) {
      return;
    }

    const schema = schemaIdToSchema.get(node.schemaId);
    if (schema) {
      const attributeIdToAttribute = getSchemaAttributesMap(schema);

      const errors = getAttributeErrors(
        { attributes: attributeIdToAttribute } as Schema,
        mapWorkflowAttributesToAttributes(node.attributes),
      );

      if (errors.size) {
        errorsByPath.set(pathKey, new Set(errors.keys()));

        ancestorPathsToExpand = combineSets(
          ancestorPathsToExpand,
          getAncestorPaths(path),
        );
      }
    }

    (node.elements ?? []).forEach((child, index) => {
      walk(child, [...path, 'elements', index]);
    });
  };

  walk(draftAsset, []);

  return { errorsByPath, ancestorPathsToExpand };
};

// removes excluded elements and their ancestors from a draft asset
export const removeExcludedElements = (
  draftAsset: DraftAsset,
  excludedPaths: Set<string>,
): DraftAsset => {
  const pruneElements = (
    elements: DraftElement[],
    parentPath: (string | number)[],
  ): DraftElement[] =>
    elements.reduce<DraftElement[]>((acc, element, index) => {
      const path = [...parentPath, 'elements', index];
      const pathKey = path.join('.');

      if (excludedPaths.has(pathKey)) {
        return acc;
      }

      return [
        ...acc,
        {
          ...element,
          elements: pruneElements(element.elements ?? [], path),
        },
      ];
    }, []);

  return {
    ...draftAsset,
    elements: pruneElements(draftAsset.elements ?? [], []),
  };
};

export const mapCreatedElementToDraftElement = (
  el: CreatedElement,
): DraftElement => ({
  name: el.name,
  schemaId: el.schemaId,
  attributes: Object.entries(el.attributes).reduce(
    (acc, [id, attr]) => ({
      ...acc,
      [id]: {
        value: attr.value as string | number | boolean | null,
        confidence: 'high' as const,
        reason: '',
      },
    }),
    {} as DraftElement['attributes'],
  ),
  elements: (el.elements ?? []).map(mapCreatedElementToDraftElement),
});

// used to update an attribute value in a draft asset at the given path
export const setAttributeValue = <T extends DraftAsset | DraftElement>(
  tree: T,
  path: (string | number)[],
  attributeId: string,
  value: unknown,
): T => R.assocPath([...path, 'attributes', attributeId, 'value'], value, tree);
